/**
 * ObservationPage — record a QC observation (POST /qc/observations, cap qc.controle.run). The server
 * auto-evaluates (Westgard → forward-hold) and returns the verdict + z + violated rules, OR a tagged
 * `degraded` result when there's no validated lot / ecart_type ≤ 0 (rendered as a clean, actionable
 * panel — not a crash). The last result is summarised (z + verdict chip + violated rules + hold flag).
 * Full 5-state DoD + the module-absent (cloud/unmounted) degradation path.
 *
 * Read-side state: this screen has no query (it's an entry form); the 5 states map to its mutation +
 * online status + the module-absent probe on submit error.
 */
import { useState, type FormEvent } from "react";
import { Button, Card, FormField, ForbiddenState, Input, OfflineState, Select, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ApiError } from "@/api";
import { QC_CAPS } from "../caps";
import { useLabId, useRecordObservation } from "../api";
import { NIVEAUX, isDegraded, type Niveau, type ObservationResult } from "../types";
import { fmtZ, isModuleAbsent, parseNum } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { VerdictBadge } from "../components/StatusBadge";
import { ModuleAbsentState } from "../components/ModuleAbsentState";

export function ObservationPage() {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRun = useCan(QC_CAPS.controleRun);
  const mutation = useRecordObservation(labId);

  const [parametre, setParametre] = useState("");
  const [niveau, setNiveau] = useState<Niveau>("niveau_1");
  const [automate, setAutomate] = useState("");
  const [valeur, setValeur] = useState("");
  const [touched, setTouched] = useState(false);
  const [result, setResult] = useState<ObservationResult | null>(null);
  const [absent, setAbsent] = useState(false);

  const parametreError = touched && parametre.trim() === "" ? t("qc.field.required") : null;
  const valeurNum = parseNum(valeur);
  const valeurError = touched && valeurNum == null ? t("qc.field.numInvalid") : null;
  const valid = parametre.trim() !== "" && valeurNum != null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canRun) return;
    mutation.mutate(
      {
        parametre_code: parametre.trim(),
        niveau,
        valeur: valeurNum,
        ...(automate.trim() ? { automate_id: automate.trim() } : {}),
      },
      {
        onSuccess: (res) => {
          setResult(res);
          if (isDegraded(res)) {
            toast.warn(t("qc.obs.degraded.title"));
            return;
          }
          const z = fmtZ(res.z);
          if (res.verdict === "rejete") toast.error(t("qc.obs.success.rejete", { z }));
          else if (res.verdict === "alerte") toast.warn(t("qc.obs.success.alerte", { z }));
          else if (res.verdict === "accepte") toast.success(t("qc.obs.success.accepte", { z }));
          else toast.info(t("qc.obs.success.generic", { z }));
        },
        onError: (err) => {
          if (err instanceof ApiError && isModuleAbsent(err)) {
            setAbsent(true);
            return;
          }
          toast.error(err instanceof ApiError ? err.message : t("qc.field.required"));
        },
      },
    );
  };

  if (!canRun) return <ForbiddenState requiredCapabilities={[QC_CAPS.controleRun]} />;

  const header = <PageHeader title={t("qc.obs.title")} subtitle={t("qc.obs.subtitle")} />;

  if (!online) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <OfflineState />
      </div>
    );
  }

  if (absent) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
        {header}
        <ModuleAbsentState />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {header}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) minmax(240px, 1fr)", gap: "var(--nv-s-4)", alignItems: "start" }}>
        <Card title={t("qc.obs.title")}>
          <form onSubmit={submit} noValidate>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
              <FormField label={t("qc.parametre")} required {...(parametreError ? { error: parametreError } : {})}>
                {(p) => (
                  <Input
                    {...p}
                    autoFocus
                    mono
                    autoComplete="off"
                    placeholder={t("qc.parametre.placeholder")}
                    value={parametre}
                    invalid={!!parametreError}
                    onChange={(e) => setParametre(e.target.value)}
                  />
                )}
              </FormField>

              <FormField label={t("qc.niveau")} required>
                {(p) => (
                  <Select {...p} value={niveau} onChange={(e) => setNiveau(e.target.value as Niveau)}>
                    {NIVEAUX.map((n) => (
                      <option key={n} value={n}>
                        {t(`qc.niveau.${n}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>

              <FormField label={`${t("qc.automate")} (${t("qc.field.optional")})`}>
                {(p) => (
                  <Input
                    {...p}
                    mono
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder={t("qc.automate.placeholder")}
                    value={automate}
                    onChange={(e) => setAutomate(e.target.value)}
                  />
                )}
              </FormField>

              <FormField
                label={t("qc.obs.field.valeur")}
                hint={t("qc.obs.field.valeur.hint")}
                required
                {...(valeurError ? { error: valeurError } : {})}
              >
                {(p) => (
                  <Input
                    {...p}
                    mono
                    inputMode="decimal"
                    value={valeur}
                    invalid={!!valeurError}
                    onChange={(e) => setValeur(e.target.value)}
                  />
                )}
              </FormField>

              <div>
                <Button type="submit" loading={mutation.isPending} disabled={!canRun || !valid}>
                  {t("qc.obs.submit")}
                </Button>
              </div>
            </div>
          </form>
        </Card>

        <ResultPanel result={result} />
      </div>
    </div>
  );
}

function ResultPanel({ result }: { result: ObservationResult | null }) {
  const { t } = useT();
  if (!result) {
    return (
      <Card title={t("qc.obs.result.title")}>
        <p style={{ color: "var(--nv-slate)", margin: 0 }}>{t("qc.chart.rules.none")}</p>
      </Card>
    );
  }

  if (isDegraded(result)) {
    const key =
      result.reason === "no_valide_lot"
        ? "qc.obs.degraded.no_valide_lot"
        : result.reason === "ecart_type_invalide"
          ? "qc.obs.degraded.ecart_type_invalide"
          : "qc.obs.degraded.no_valide_lot";
    return (
      <Card title={t("qc.obs.degraded.title")}>
        <p
          role="alert"
          style={{
            margin: 0,
            padding: "var(--nv-s-3)",
            borderRadius: "var(--nv-r-sm)",
            background: "var(--nv-tint-high)",
            color: "var(--nv-high)",
          }}
        >
          <span aria-hidden style={{ marginInlineEnd: "var(--nv-s-2)" }}>
            ⚠
          </span>
          {t(key)}
        </p>
      </Card>
    );
  }

  const holdOpen = result.retenue_id != null;
  return (
    <Card title={t("qc.obs.result.title")}>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--nv-s-2) var(--nv-s-4)", margin: 0 }}>
        <dt style={{ color: "var(--nv-slate)" }}>{t("qc.obs.result.verdict")}</dt>
        <dd style={{ margin: 0 }}>
          <VerdictBadge verdict={result.verdict} />
        </dd>

        <dt style={{ color: "var(--nv-slate)" }}>{t("qc.obs.result.z")}</dt>
        <dd className="nv-ltr-island" style={{ margin: 0, fontFamily: "var(--nv-font-mono)" }}>
          {fmtZ(result.z)}
        </dd>

        <dt style={{ color: "var(--nv-slate)" }}>{t("qc.obs.result.rules")}</dt>
        <dd className="nv-ltr-island" style={{ margin: 0, color: result.regles_violees.length ? "var(--nv-critical)" : "var(--nv-ink)" }}>
          {result.regles_violees.length ? result.regles_violees.join(", ") : t("qc.chart.rules.none")}
        </dd>

        <dt style={{ color: "var(--nv-slate)" }}>{t("qc.obs.result.hold")}</dt>
        <dd style={{ margin: 0, color: holdOpen ? "var(--nv-critical)" : "var(--nv-ink)" }}>
          {holdOpen ? t("qc.obs.result.hold.yes") : t("qc.obs.result.hold.no")}
        </dd>
      </dl>
    </Card>
  );
}
